import { sdk } from '@/lib/api';
import { FetchError } from '@/lib/api/client';
import { queryKeysFactory } from '@/lib/query-key-factory';
import { HttpTypes } from '@/types';
import { IUploadFileImage, UnsplashPhoto } from '@/types/general';
import { IAsset } from '@/types/site';
import {
  QueryKey,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import Cookies from 'js-cookie';

const FILE_SERVICES_KEY = 'file-service' as const;
const UNSPLASH_KEY = 'unsplash' as const;

export const fileServiceQuery = queryKeysFactory(FILE_SERVICES_KEY);
export const unsplashQuery = queryKeysFactory(UNSPLASH_KEY);

const isPartner = Cookies.get('_partner_id') !== undefined;

export const useGetAssets = (
  query: HttpTypes.FindParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.IResponse<IAsset[]>,
      FetchError,
      HttpTypes.IResponse<IAsset[]>,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: fileServiceQuery.list(query),
    queryFn: async () =>
      isPartner ? sdk.partner.fileService.list(query) : sdk.admin.fileService.list(query),
    ...options,
  });

  return { data, ...rest };
};

export const useUploadImage = (
  options?: UseMutationOptions<HttpTypes.IResponse<IUploadFileImage>, FetchError, FormData, unknown>
) => {
  return useMutation({
    mutationFn: (data) =>
      isPartner
        ? sdk.partner.fileService.uploadImage(data)
        : sdk.admin.fileService.uploadImage(data),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useGetUnsplash = (
  query: HttpTypes.FindParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.IResponse<UnsplashPhoto[]>,
      FetchError,
      HttpTypes.IResponse<UnsplashPhoto[]>,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: unsplashQuery.list(query),
    queryFn: async () => sdk.admin.fileService.unsplashImage(query),
    ...options,
  });

  return { data, ...rest };
};

export const useUploadPublicImage = (
  options?: UseMutationOptions<HttpTypes.IResponse<IUploadFileImage>, FetchError, FormData, unknown>
) => {
  return useMutation({
    mutationFn: (data) => sdk.site.general.uploadImage(data),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};
